// @flow

import { PermissionEnum } from "@modules/account/components";
import type { State } from "@store/reducers";
import type { Profile, ProfileUser, ProfileClient } from "../types";
import { formatAuthoredBy } from "@transformers/names";
import { USER_TYPES } from "@modules/account/constants";
import { getClient } from "@modules/recording/selectors";

export const isAuthChecked = (state: State): boolean => state.account.authChecked;

export const isAuthenticated = (state: State): boolean => state.account.loggedIn;

export const isAuthenticatedForFirstTime: (state: State) => boolean = (state: State) => {
  return (getProfile(state) || {}).isLoggedInForFirstTime;
};

export const isLoading = (state: State): boolean => state.account.isLoading;

export const getPermissions = (state: State): { [permission: string]: boolean } | null =>
  (getProfile(state) || {}).permissions;

export const hasPermission = (state: State, permissionId: string): boolean => {
  return !!(getPermissions(state) || {})[permissionId];
};

export const isFirstCareUser: (state: State) => boolean = (state: State) =>
  hasPermission(state, "app.global.context.switch");

export const hasClientContextSwitch = (state: State): boolean =>
  hasPermission(state, PermissionEnum.APP_GLOBAL_CONTEXT_SWITCH);

export const hasReportingClientContextSwitch = (state: State): boolean => {
  return (
    isFirstCareUser(state) &&
    [USER_TYPES.SERVICE_DELIVERY_ADVISOR, USER_TYPES.SERVICE_MANAGER, USER_TYPES.ADMIN].includes(
      getUserType(state),
    )
  );
};

export const getError = (state: State): string | null => state.account.error;

export const getProfile = (state: State): Profile | null => state?.account?.profile;

export const getUser = (state: State): ?Object => (getProfile(state) || {}).user;

export const getUserId = (state: State): number | null => (getUser(state) || {}).id;

export const getUserType = (state: State): string => (getUser(state) || {}).userType;

export const isUserNurseAdvisor = (state: State): boolean =>
  [USER_TYPES.NURSE_ADVISOR, USER_TYPES.LEAD_NURSE_ADVISOR].includes(getUserType(state));

export const isNurceAdvisorOrContactCentreManager = (state: State) => {
  const userType = (((state.account || {}).profile || {}).user || {}).userType;

  return [
    USER_TYPES.ADMIN,
    USER_TYPES.NURSE_ADVISOR,
    USER_TYPES.LEAD_NURSE_ADVISOR,
    USER_TYPES.CONTACT_CENTRE_MANAGER,
  ].includes(userType);
};
export const selectClientService = (state: State): string | null =>
  (getClient(state) || {}).serviceType;
export const isAllowedToTriage = (state: State): boolean => {
  return (
    selectClientService(state) === "COMPLETE_SUPPORT" &&
    [
      USER_TYPES.NURSE_ADVISOR,
      USER_TYPES.LEAD_NURSE_ADVISOR,
      USER_TYPES.CONTACT_CENTRE_MANAGER,
    ].includes(getUserType(state))
  );
};

export const isUserAbsenceAdvisor = (state: State): boolean =>
  [
    USER_TYPES.ADMIN,
    USER_TYPES.ABSENCE_ADVISOR,
    USER_TYPES.CONTACT_CENTRE_MANAGER,
    USER_TYPES.SERVICE_MANAGER,
  ].includes(getUserType(state));

export const getUserClient = (state: State) => ((getProfile(state) || {}).user || {}).client;

export const getUserClientId = (state: State): number | null => {
  return ((getProfile(state) || {}).user || {}).clientId;
};

export const getFirstName = (state: State): string =>
  ((getProfile(state) || {}).user || {}).firstName || "Customer";

export const getJobTitle = (state: State): string =>
  ((getUser(state) || {}).employeePositions || []).map(pos => pos.jobTitle).join(", ");

export const getTaskCount = (state: State): number => (getProfile(state) || {}).taskCount || 0;

export const getDisplayName = (state: State): string => {
  const profile = ((state.account || {}).profile || {}).user || { firstName: "", lastName: "" };
  const name = { firstName: profile.firstName, lastName: profile.lastName };
  return formatAuthoredBy(name) || "";
};

export const userEmployeePositions = (state: State) => (getUser(state) || {}).employeePositions;

export const getUserName = (state: State): string => {
  const profile = ((state.account || {}).profile || {}).user || { firstName: "" };

  return profile.firstName;
};

/**
 * This selector will return one of:
 * user types
 ADMIN
 ABSENCE_ADVISOR
 NURSE_ADVISOR
 LEAD_NURSE_ADVISOR
 CONTACT_CENTRE_MANAGER
 SERVICE_MANAGER
 TEXT_TO_CLOSE_SERVICE
 EMPLOYEE
 MANAGER
 ADVANCED_USER
 FULL_HR_ACCESS
 CLIENT_ADMIN
 CLIENT_DATA_TRANSFER
 CLIENT_TREND_VIEWER
 CLIENT_SUPPLIER
 DEVELOPER
 */
export const selectUserType = (state: State): string => {
  return (((state.account || {}).profile || {}).user || {}).userType || "";
};

export const isNurse = (state: State): boolean => {
  const userType = selectUserType(state);
  return userType === USER_TYPES.NURSE_ADVISOR || userType === USER_TYPES.LEAD_NURSE_ADVISOR;
};

export const getUserTraits = (state: State): Array<string> =>
  (state.account.profile || {}).traits || [];

export const registerFormData = (state: State): Object => state.form.register.values;

export const termsOfUseModalVisible = (state: State): boolean =>
  state.account.modals.termsOfUse.visible;
export const getTokenExpiresAt = (state: State): string =>
  (getProfile(state) || {}).accessTokenExpiresAt;

export const isClientAdmin = (state: State): boolean =>
  getUserType(state) === USER_TYPES.CLIENT_ADMIN;

export const hasDefaultClient = (state: State): boolean =>
  !![
    "CLIENT_ADMIN",
    "FULL_HR_ACCESS",
    "ADVANCED_USER",
    "MANAGER",
    "CLIENT_DATA_TRANSFER",
    "CLIENT_TREND_VIEWER",
    "CLIENT_SUPPLIER",
  ].includes(getUserType(state));

export const getIsImpersonateLogin: (state: State) => boolean = state =>
  state.account.impersonateLogin;

export const hasWellbeingReferralDetailsAccess = (state: State) => {
  return [
    USER_TYPES.ADMIN,
    USER_TYPES.CLIENT_ADMIN,
    USER_TYPES.SERVICE_MANAGER,
    USER_TYPES.FULL_HR_ACCESS,
  ].includes(getUserType(state));
};

export const contactModalVisible: (state: State) => boolean = state => {
  return state.account.modals.contact.visible;
};

export const accessReportingDashboardOverviewData = (state: State) => {
  return [
    // Operating company:
    USER_TYPES.ADMIN,
    USER_TYPES.CONTACT_CENTRE_MANAGER,
    USER_TYPES.SERVICE_MANAGER,

    // Client company:
    USER_TYPES.ADVANCED_USER,
    USER_TYPES.CLIENT_ADMIN,
    USER_TYPES.FULL_HR_ACCESS,
    USER_TYPES.MANAGER,
    USER_TYPES.CLIENT_SUPPLIER,
  ].includes(getUserType(state));
};
